import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  EnvironmentInjector,
  inject,
  inputBinding,
  Type,
  Service,
} from '@angular/core';
import { ComponentInputs, MODAL_CLOSE_EVENT } from './modal.constants';

@Service()
export class ModalService {
  #appRef = inject(ApplicationRef);

  #environmentInjector = inject(EnvironmentInjector);

  #componentRef?: ComponentRef<unknown>;

  #domElement?: HTMLElement;

  openModal<T extends object>(
    component: Type<T>,
    inputs: ComponentInputs<T> = {},
  ): { firstModal: HTMLElement | null; instance: T } {
    if (this.#componentRef) {
      this.removeModal();
    }

    const componentRef = createComponent(component, {
      environmentInjector: this.#environmentInjector,
      bindings: Object.entries(inputs).map(([key, value]) =>
        inputBinding(key, () => value),
      ),
    });

    this.#componentRef = componentRef;

    // Append the modal element to the DOM
    const viewRef = componentRef.hostView as EmbeddedViewRef<T>;

    // Attach the modal to the application view for lifecycle hooks
    this.#appRef.attachView(viewRef);
    this.#domElement = viewRef.rootNodes[0] as HTMLElement;
    this.#domElement?.addEventListener(MODAL_CLOSE_EVENT, () => {
      this.removeModal();
    });

    document.body.appendChild(this.#domElement);

    const firstModal = this.#domElement.querySelector(
      'a-content-modal',
    ) as HTMLElement | null;

    return { firstModal, instance: componentRef.instance };
  }

  removeModal(): void {
    if (this.#componentRef) {
      this.#appRef.detachView(this.#componentRef.hostView);
      this.#componentRef.destroy();
      this.#componentRef = undefined;
    }
  }
}
